import { z } from 'zod';

/** Required audit reason for the destructive node-delete operation. */
export const NodeDeleteRemarkSchema = z.object({
  remark: z
    .string({ message: '請輸入刪除備註' })
    .trim()
    .min(1, '請輸入刪除備註')
    .max(500, '刪除備註不可超過 500 個字元'),
});

export type NodeDeleteRemarkValues = z.infer<typeof NodeDeleteRemarkSchema>;

/**
 * 節點動態協議 schema — 對應 `nodeConfigJson.protocols` 陣列。
 * 客戶端會根據此陣列動態生成 sing-box outbound，作為 urltest fallback 層。
 *
 * 目前支援 3 種協議（後端 / sing-box 均已支援，但生產節點尚未部署）：
 *  - trojan         : Trojan + TLS（port 2083）
 *  - hysteria2      : Hysteria2 QUIC（port 5443）
 *  - vless-reality  : VLESS + Reality TLS（port 2087）
 */
const portField = z
  .number({ message: '請輸入端口' })
  .int('端口必須為整數')
  .min(1, '端口範圍為 1-65535')
  .max(65535, '端口範圍為 1-65535');

const tagField = z
  .string()
  .min(1, '請輸入 tag')
  .max(32, 'tag 不可超過 32 個字元');

const sniField = z
  .string()
  .min(1, '請輸入 SNI')
  .max(253, 'SNI 不可超過 253 個字元');

const passwordField = z.string().min(1, '請輸入密碼');

const priorityField = z
  .number({ message: '請輸入優先級' })
  .int('優先級必須為整數')
  .min(1, '優先級範圍為 1-99')
  .max(99, '優先級範圍為 1-99')
  .optional();

const emptyToUndefined = (value: unknown) => {
  if (typeof value === 'string' && value.trim() === '') return undefined;
  return value;
};

const trimEmptyToUndefined = (value: unknown) => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

const optionalStringField = z.preprocess(
  emptyToUndefined,
  z.string().min(1).optional()
);

const optionalTrimmedStringField = z.preprocess(
  trimEmptyToUndefined,
  z.string().min(1).optional()
);

const dnsTxtStringListField = z.preprocess(value => {
  if (typeof value === 'string') {
    return value
      .split(/[\n,]/)
      .map(item => item.trim())
      .filter(Boolean);
  }
  if (Array.isArray(value)) {
    return value.map(String).map(item => item.trim()).filter(Boolean);
  }
  return value;
}, z.array(z.string().min(1)).default([]));

export const DnsTxtDohEndpointSchema = z.preprocess(
  value => (typeof value === 'string' ? value.trim() : value),
  z
    .string()
    .superRefine((value, ctx) => {
      let url: URL;
      try {
        url = new URL(value);
      } catch {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'DNS TXT DoH endpoint 必須是 HTTPS URL',
        });
        return;
      }
      if (
        url.protocol !== 'https:' ||
        !url.hostname ||
        url.username ||
        url.password
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'DNS TXT DoH endpoint 必須是 HTTPS URL，且不可包含帳號密碼',
        });
      }
    })
    .transform(value => new URL(value).toString())
);

const dnsTxtDohEndpointListField = z.preprocess(value => {
  if (typeof value === 'string') {
    return value
      .split(/[\n,]/)
      .map(item => item.trim())
      .filter(Boolean);
  }
  if (Array.isArray(value)) {
    return value.map(String).map(item => item.trim()).filter(Boolean);
  }
  return value;
}, z.array(DnsTxtDohEndpointSchema).default([]));

const Hysteria2ObfsSchema = z
  .object({
    type: z.literal('salamander').optional().default('salamander'),
    password: optionalStringField,
  })
  .transform(value =>
    value.password
      ? {
          type: 'salamander' as const,
          password: value.password,
        }
      : undefined
  )
  .optional();

const Hysteria2ServerPortsSchema = z.preprocess(value => {
  if (typeof value === 'string') {
    return value
      .split(/[\n,]/)
      .map(item => item.trim())
      .filter(Boolean);
  }
  return value;
}, z.array(z.string().regex(/^\d{1,5}(:\d{1,5})?$/, '請輸入合法 port 或 port range')).optional());

const TextListSchema = z.preprocess(value => {
  if (typeof value === 'string') {
    return value
      .split(/[\n,]/)
      .map(item => item.trim())
      .filter(Boolean);
  }
  return value;
}, z.array(z.string().min(1)).optional());

// 歷史 JSON 原樣保存；不得補旗標、清掉 partial 或 strip 未知欄位。
const Hysteria2ProductionProofSchema = z.record(z.unknown()).optional();

const TrojanProtocolSchema = z.object({
  type: z.literal('trojan'),
  tag: tagField,
  server: optionalStringField,
  priority: priorityField,
  port: portField,
  password: passwordField,
  sni: sniField,
});

const Hysteria2ProtocolSchema = z.object({
  type: z.literal('hysteria2'),
  tag: tagField,
  server: optionalStringField,
  priority: priorityField,
  productionVisible: z.boolean().default(false),
  port: portField,
  password: passwordField,
  sni: sniField,
  obfs: Hysteria2ObfsSchema,
  server_ports: Hysteria2ServerPortsSchema,
  hop_interval: z.preprocess(
    emptyToUndefined,
    z
      .string()
      .regex(/^\d+(ms|s|m|h)$/, '例如 30s、500ms、5m')
      .optional()
  ),
  up_mbps: z.preprocess(emptyToUndefined, z.number().int().min(1).max(100000).optional()),
  down_mbps: z.preprocess(emptyToUndefined, z.number().int().min(1).max(100000).optional()),
  productionProof: Hysteria2ProductionProofSchema,
});

/**
 * REALITY dest（Aparecium NewSessionTicket 偵測對策，2026-04-27）
 * 2025-06 公開的 Aparecium 工具可透過 TLS 1.3 NewSessionTicket 握手後簽名識別 REALITY；
 * microsoft.com 會回傳 NewSessionTicket，BoringSSL 系列伺服器（dl.google.com / *.gstatic.com）不會。
 * 客戶端優先使用 dest.server 作為 tls.server_name 與 reality 偽裝目標；
 * dest 為空時 fallback 到 sni（向後相容舊節點）。
 * 詳見 docs/research/anti-blocking-2026-04-27/expert-01-active-probing-defense.md P0 #2。
 */
const RealityDestSchema = z.object({
  server: z.string().min(1, '請輸入 Reality dest 上游伺服器'),
  port: portField.optional(),
  type: z.enum(['no-ticket', 'with-ticket']).optional(),
});

const VlessRealityProtocolSchema = z.object({
  type: z.literal('vless-reality'),
  tag: tagField,
  server: optionalStringField,
  priority: priorityField,
  port: portField,
  uuid: z.string().uuid('請輸入合法 UUID'),
  flow: z.literal('xtls-rprx-vision'),
  sni: sniField,
  public_key: z.string().min(1, '請輸入 Reality public key'),
  short_id: z
    .string()
    .min(1, '請輸入 short id')
    .regex(/^[0-9a-fA-F]*$/, 'short id 必須為 hex 字串'),
  dest: RealityDestSchema.optional(),
});

/**
 * AB-T2 / D3 — amneziaWG (9th outbound) Schema
 *
 * 對應 normalize 規則（見 `vpn-manage-server/src/service/ip.service.ts`
 * `normalizeAmneziaWG`）以及 migration `1711756800057-AmneziaWGSchema`：
 *
 *   - privateKey / peers[].publicKey / peers[].presharedKey: base64 32-byte
 *     WG key（44 chars，符合 `[A-Za-z0-9+/]{43}=`）
 *   - localAddresses / peers[].allowedIPs: CIDR 陣列
 *   - junkCount: 0..256（0=disabled）
 *   - junkMinSize / junkMaxSize: 16..2000，max ≥ min
 *   - h1..h4: 32-bit unsigned hex（最多 8 chars 或數值）
 *     至少一個必須 ≠ WG stock value
 *   - mtu: 1280..1500
 *   - s1Hex / s2Hex: hex string，≤ 256 bytes（512 hex chars）
 */
const WG_KEY_REGEX = /^[A-Za-z0-9+/]{43}=$/;
const HEX_REGEX = /^[0-9a-fA-F]*$/;
const CIDR_REGEX = /^([0-9a-fA-F:.]+)\/([0-9]{1,3})$/;
const HEX32_REGEX = /^(0x|0X)?[0-9a-fA-F]{1,8}$/;

const wgKeyField = z
  .string()
  .min(1, '請輸入 base64 WG key')
  .regex(WG_KEY_REGEX, '必須為 base64 32-byte WG key（44 chars 含 = padding）');

const optionalWgKeyField = z.preprocess(
  emptyToUndefined,
  z
    .string()
    .regex(WG_KEY_REGEX, '必須為 base64 32-byte WG key（44 chars 含 = padding）')
    .optional()
);

const cidrField = z
  .string()
  .min(1)
  .regex(CIDR_REGEX, '必須為合法 CIDR（例：10.0.0.2/32 或 0.0.0.0/0）');

const cidrListField = z.preprocess(value => {
  if (typeof value === 'string') {
    return value
      .split(/[\n,]/)
      .map(item => item.trim())
      .filter(Boolean);
  }
  if (Array.isArray(value)) {
    return value.map(String).map(item => item.trim()).filter(Boolean);
  }
  return value;
}, z.array(cidrField).min(1, '至少需要 1 個 CIDR'));

const optionalHex32Field = z.preprocess(
  trimEmptyToUndefined,
  z.string().regex(HEX32_REGEX, '必須為最多 8 chars 的 hex（可含 0x 前綴）').optional()
);

const optionalHexBytesField = z.preprocess(
  value => {
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
  },
  z
    .string()
    .regex(HEX_REGEX, '必須為合法 hex 字串（0-9a-f）')
    .refine(v => v.length % 2 === 0, 'hex 長度必須為偶數')
    .refine(v => v.length / 2 <= 256, '長度必須 ≤ 256 bytes（512 hex chars）')
    .optional()
);

const AmneziaWGPeerSchema = z.object({
  publicKey: wgKeyField,
  presharedKey: optionalWgKeyField,
  allowedIPs: cidrListField,
  serverEndpoint: optionalStringField,
  serverPort: z.preprocess(
    emptyToUndefined,
    z.number().int().min(1).max(65535).optional()
  ),
  persistentKeepaliveInterval: z.preprocess(
    emptyToUndefined,
    z.number().int().min(0).max(65535).optional()
  ),
});

const AmneziaWGProtocolSchema = z
  .object({
    type: z.literal('amneziawg'),
    tag: tagField,
    server: optionalStringField,
    priority: priorityField,
    port: portField,
    privateKey: wgKeyField,
    localAddresses: cidrListField,
    peers: z.array(AmneziaWGPeerSchema).min(1, '至少需要 1 個 peer'),
    junkCount: z.preprocess(
      emptyToUndefined,
      z.number().int().min(0).max(256).optional()
    ),
    junkMinSize: z.preprocess(
      emptyToUndefined,
      z.number().int().min(16).max(2000).optional()
    ),
    junkMaxSize: z.preprocess(
      emptyToUndefined,
      z.number().int().min(16).max(2000).optional()
    ),
    s1Hex: optionalHexBytesField,
    s2Hex: optionalHexBytesField,
    h1: optionalHex32Field,
    h2: optionalHex32Field,
    h3: optionalHex32Field,
    h4: optionalHex32Field,
    mtu: z.preprocess(
      emptyToUndefined,
      z.number().int().min(1280).max(1500).optional()
    ),
  })
  .superRefine((value, ctx) => {
    if (
      typeof value.junkMinSize === 'number' &&
      typeof value.junkMaxSize === 'number' &&
      value.junkMaxSize < value.junkMinSize
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['junkMaxSize'],
        message: 'junkMaxSize 必須 ≥ junkMinSize',
      });
    }
    // 至少一個 h1..h4 ≠ WG stock value（0x01000000 / 0x02000000 / 0x03000000 / 0x04000000）
    const stocks = ['1000000', '2000000', '3000000', '4000000'];
    const h1 = (value.h1 ?? '').toString().replace(/^0x/i, '').toLowerCase();
    const h2 = (value.h2 ?? '').toString().replace(/^0x/i, '').toLowerCase();
    const h3 = (value.h3 ?? '').toString().replace(/^0x/i, '').toLowerCase();
    const h4 = (value.h4 ?? '').toString().replace(/^0x/i, '').toLowerCase();
    if (
      h1 === stocks[0] &&
      h2 === stocks[1] &&
      h3 === stocks[2] &&
      h4 === stocks[3]
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['h1'],
        message: 'h1..h4 全部等於 WG stock value，fingerprint defense 等於 no-op',
      });
    }
  });

/**
 * NOTE: amneziawg schema 用 superRefine 做跨欄位驗證（junkMaxSize ≥ junkMinSize、
 * h1..h4 not all stock），但 z.discriminatedUnion 不接受 ZodEffects 成員。
 * 改用 z.union + 預先以 type 分流。對 BO 使用情境（form 提交前 type 已知）等價。
 */
export const NodeProtocolSchema = z.union([
  TrojanProtocolSchema,
  Hysteria2ProtocolSchema,
  VlessRealityProtocolSchema,
  AmneziaWGProtocolSchema,
]);

/** 提交時才檢查發布安全，讀取舊節點不可因此靜默丟掉整個協議。 */
export const NodeProtocolsDeliverySchema = z.array(NodeProtocolSchema).superRefine((protocols, ctx) => {
  protocols.forEach((protocol, index) => {
    if (protocol.type === 'hysteria2' && protocol.productionVisible &&
      (!protocol.obfs || !protocol.obfs.password?.trim())) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'obfs', 'password'],
        message: '下發 Hysteria2 必須設定 Salamander 密碼' });
    }
  });
});

export type NodeProtocol = z.infer<typeof NodeProtocolSchema>;
export type NodeProtocolType = NodeProtocol['type'];

/** 相容既有呼叫者；proof 已為選填歷史資料，不再清理不完整物件。 */
export function omitIncompleteHysteria2ProductionProof(
  protocols: NodeProtocol[]
): NodeProtocol[] {
  return protocols;
}

export const FALLBACK_PRIORITY_OPTIONS = [
  { value: 'sk6', label: 'SK6 主路徑' },
  { value: 'sk6-ws', label: 'SK6-WS / CDN' },
  { value: 'vless-reality', label: 'VLESS Reality' },
  { value: 'anytls', label: 'AnyTLS' },
  { value: 'hysteria2', label: 'Hysteria2' },
  { value: 'trojan', label: 'Trojan' },
  { value: 'shadowtls', label: 'ShadowTLS' },
  { value: 'vless-worker', label: 'VLESS Worker' },
] as const;

const fallbackPriorityValue = z.enum([
  'sk6',
  'vless-reality',
  'anytls',
  'hysteria2',
  'sk6-ws',
  'trojan',
  'shadowtls',
  'vless-worker',
]);

export type FallbackPriorityValue = z.infer<typeof fallbackPriorityValue>;

export const DEFAULT_FALLBACK_PRIORITY: FallbackPriorityValue[] = [
  'sk6',
  'sk6-ws',
  'vless-reality',
  'anytls',
  'hysteria2',
];

export const CdnRelaySchema = z.object({
  enabled: z.boolean().default(false),
  domain: z.string().min(1, '請輸入 CDN domain').optional(),
  port: portField.default(443),
  wsPort: portField.default(80),
  wsPath: z.string().min(1).default('/'),
});

export const AnyTlsSchema = z.object({
  enabled: z.boolean().default(false),
  domain: z.string().min(1, '請輸入 AnyTLS domain').optional(),
  port: portField.default(443),
  password: z.string().optional(),
  sni: z.string().optional(),
});

export const DnsTxtDiscoverySchema = z.object({
  enabled: z.boolean().default(true),
  names: dnsTxtStringListField,
  dohEndpoints: dnsTxtDohEndpointListField,
  maxAgeSec: z.number().int().min(60).max(86400).default(900),
  expectedKid: optionalTrimmedStringField,
}).superRefine((value, ctx) => {
  if (!value.enabled) return;
  if (value.names.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['names'],
      message: '啟用 DNS TXT discovery 必須設定 TXT names',
    });
  }
  if (value.dohEndpoints.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['dohEndpoints'],
      message: '啟用 DNS TXT discovery 必須設定 HTTPS DoH endpoint',
    });
  }
  if (value.enabled && !value.expectedKid) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['expectedKid'],
      message: '啟用 DNS TXT discovery 必須設定 Expected Key ID',
    });
  }
});

export const ConnectionRotationSchema = z.object({
  enabled: z.boolean().default(false),
  minMinutes: z.number().int().min(15).max(1440).default(15),
  maxMinutes: z.number().int().min(15).max(1440).default(30),
});

/**
 * Sprint 19 Wave 6 (W6-A) — DAITA (Defense Against AI Traffic Analysis).
 *
 * Per-node Maybenot v2 machine-spec authoring. Mirrors server-side
 * `IpService.normalizeDaita` contract: five fields (en/ms/rms/mpf/mbf) on
 * the wire compact envelope, full names here for human authoring. The
 * client native stamper (Np.kt applyDaita on Android; box.swift
 * applyDaitaIos on iOS) reads the compact `da` key emitted by
 * `compactDaitaConfig`; the BO-side full-name shape is preserved for
 * audit-log readability.
 *
 * Wave 6 = authoring only — no production node has `enabled=true` until
 * the W6-B ACL union + libbox WITH_DAITA env gate flip.
 *
 * Reference: docs/research/daita-cross-platform-port.md §2.1
 */
export const DaitaSchema = z.object({
  enabled: z.boolean().default(false),
  machineSpec: z.string().max(16384, 'machineSpec 長度不可超過 16384 字').default(''),
  replyMachineSpec: z
    .string()
    .max(16384, 'replyMachineSpec 長度不可超過 16384 字')
    .default(''),
  maxPaddingFrac: z
    .number()
    .min(0, 'maxPaddingFrac 範圍為 0.0-1.0')
    .max(1, 'maxPaddingFrac 範圍為 0.0-1.0')
    .default(0.5),
  maxBlockingFrac: z
    .number()
    .min(0, 'maxBlockingFrac 範圍為 0.0-1.0')
    .max(1, 'maxBlockingFrac 範圍為 0.0-1.0')
    .default(0.1),
}).superRefine((value, ctx) => {
  if (value.enabled && !value.machineSpec.trim() && !value.replyMachineSpec.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['machineSpec'],
      message: '啟用 DAITA 時必須至少設定 machineSpec 或 replyMachineSpec',
    });
  }
});

/**
 * AB-D6 — per-protocol uTLS fingerprint override map.
 *
 * Lets ops shift fingerprints from BO instead of cutting a client release
 * when GFW learns a JA4 hash. Wire keys are protocol type strings; values
 * are uTLS labels supported by sing-box (`utls_client.go uTLSClientHelloID`).
 * Empty / undefined values mean "use the client's compiled-in default".
 */
export const FINGERPRINT_VALUE_OPTIONS = [
  { value: '', label: '使用客戶端預設' },
  { value: 'chrome', label: 'chrome' },
  { value: 'firefox', label: 'firefox' },
  { value: 'safari', label: 'safari' },
  { value: 'edge', label: 'edge' },
  { value: '360', label: '360' },
  { value: 'qq', label: 'qq' },
  { value: 'ios', label: 'ios' },
  { value: 'android', label: 'android' },
  { value: 'random', label: 'random' },
  { value: 'randomized', label: 'randomized' },
] as const;

export type FingerprintValue = Exclude<
  (typeof FINGERPRINT_VALUE_OPTIONS)[number]['value'],
  ''
>;

export const FINGERPRINT_PROTOCOL_KEYS = [
  'sk6',
  'sk6-ws',
  'shadowtls',
  'anytls',
  'trojan',
  'vless',
  'vless-reality',
  'hysteria2',
] as const;

export type FingerprintProtocolKey = (typeof FINGERPRINT_PROTOCOL_KEYS)[number];

const fingerprintValueField = z.enum([
  'chrome',
  'firefox',
  'safari',
  'edge',
  '360',
  'qq',
  'ios',
  'android',
  'random',
  'randomized',
]);

export const FingerprintMapSchema = z
  .record(z.enum(FINGERPRINT_PROTOCOL_KEYS), fingerprintValueField)
  .optional();

export type FingerprintMap = Partial<Record<FingerprintProtocolKey, FingerprintValue>>;

/** UI-friendly labels per protocol — used by the BO node-form dropdowns. */
export const FINGERPRINT_PROTOCOL_LABELS: Record<FingerprintProtocolKey, string> = {
  'sk6': 'SK6 直連',
  'sk6-ws': 'SK6-WS / CDN',
  'shadowtls': 'ShadowTLS',
  'anytls': 'AnyTLS',
  'trojan': 'Trojan',
  'vless': 'VLESS-WS (CF Workers)',
  'vless-reality': 'VLESS Reality',
  'hysteria2': 'Hysteria2',
};

export const NodeSk6ConfigSchema = z
  .object({
    relayOnly: z.boolean().optional(),
    originHidden: z.boolean().optional(),
    allowDirect: z.boolean().optional(),
    cdnRelay: CdnRelaySchema.optional(),
    anytls: AnyTlsSchema.optional(),
    dnsTxtDiscovery: DnsTxtDiscoverySchema.optional(),
    connectionRotation: ConnectionRotationSchema.optional(),
    rotation: ConnectionRotationSchema.optional(),
    fallbackPriority: z.array(fallbackPriorityValue).optional(),
    protocols: z.array(NodeProtocolSchema).optional(),
    fingerprintMap: FingerprintMapSchema,
    /** Sprint 19 Wave 6 (W6-A) — DAITA per-node authoring. */
    daita: DaitaSchema.optional(),
  })
  .passthrough();

export type NodeSk6Config = z.infer<typeof NodeSk6ConfigSchema>;

/** 依協議類型產生預設 row（新增按鈕下拉使用） */
export function makeDefaultProtocol(type: NodeProtocolType): NodeProtocol {
  switch (type) {
    case 'trojan':
      return {
        type: 'trojan',
        tag: 'trojan',
        server: undefined,
        priority: 5,
        port: 2083,
        password: '',
        sni: 'endpoint.swiftmesh.net',
      };
    case 'hysteria2':
      return {
        type: 'hysteria2',
        tag: 'hysteria2',
        server: undefined,
        priority: 4,
        productionVisible: false,
        port: 5443,
        password: '',
        sni: 'endpoint.swiftmesh.net',
        obfs: undefined,
        server_ports: undefined,
        hop_interval: undefined,
        up_mbps: undefined,
        down_mbps: undefined,
        productionProof: undefined,
      };
    case 'vless-reality':
      return {
        type: 'vless-reality',
        tag: 'reality',
        server: undefined,
        priority: 2,
        port: 2087,
        uuid: '00000000-0000-0000-0000-000000000000',
        flow: 'xtls-rprx-vision',
        // 注意：sni 仍保留為填寫欄位（顯示 / fallback），但 client 端實際偽裝對象
        // 已切換為 dest.server。新節點預設 dest = dl.google.com（BoringSSL，no-ticket）
        // 對抗 Aparecium NewSessionTicket 偵測；舊節點若無 dest，client fallback 到 sni。
        sni: 'www.microsoft.com',
        public_key: '',
        short_id: '',
        dest: {
          server: 'dl.google.com',
          port: 443,
          type: 'no-ticket',
        },
      };
    case 'amneziawg':
      // 注意：localAddresses / peers[].allowedIPs schema 透過 preprocess 接受
      // string（每行 / 逗號分隔）或 array，但表單預設值用 array，與其他既有欄位
      // 一致；TextArea 顯示時會用 `\n` join，提交時 schema 自動 split。
      return {
        type: 'amneziawg',
        tag: 'amneziawg',
        server: undefined,
        priority: 6,
        port: 51820,
        privateKey: '',
        localAddresses: ['10.0.0.2/32'],
        peers: [
          {
            publicKey: '',
            presharedKey: undefined,
            allowedIPs: ['0.0.0.0/0', '::/0'],
            serverEndpoint: 'endpoint.swiftmesh.net',
            serverPort: 51820,
            persistentKeepaliveInterval: undefined,
          },
        ],
        // 對應 AmneziaVPN 文件建議 Russia 場景的 4..15 區間
        junkCount: 4,
        junkMinSize: 50,
        junkMaxSize: 1000,
        s1Hex: undefined,
        s2Hex: undefined,
        // operator 必須執行 hkdf-derive.sh 產生 per-node 值（見 runbook §D3）
        // 預設留空，BO 會接受未設置；但若全部設成 stock 值會被 normalize 阻擋
        h1: undefined,
        h2: undefined,
        h3: undefined,
        h4: undefined,
        mtu: 1280,
      };
    default: {
      const _exhaustive: never = type;
      throw new Error(`unknown protocol type: ${_exhaustive as string}`);
    }
  }
}

export const NODE_PROTOCOL_TYPE_OPTIONS: Array<{
  value: NodeProtocolType;
  label: string;
}> = [
  { value: 'trojan', label: 'Trojan + TLS' },
  { value: 'hysteria2', label: 'Hysteria2 (QUIC)' },
  { value: 'vless-reality', label: 'VLESS + Reality' },
  { value: 'amneziawg', label: 'AmneziaWG (UDP)' },
];
